import type { ReactNode } from 'react';

/**
 * Minimal inline markdown → React nodes for article text: `**bold**`, `*italic*`
 * (or `_italic_`), `` `code` `` and `[label](href)`.
 *
 * Deliberately *not* a full markdown implementation and deliberately not
 * `dangerouslySetInnerHTML`: the output is React elements, so authored content can
 * never inject markup. Anything unrecognised stays literal text.
 */

/**
 * One alternation, matched in priority order: code first so `**` inside a code
 * span stays literal.
 *
 * Emphasis delimiters must hug their text (`(?!\s)` … `(?<!\s)`), otherwise
 * arithmetic like `2 * 3 * 4` would render as italics. Underscores additionally
 * require a non-word boundary so `snake_case_names` are left alone.
 */
const TOKEN = new RegExp(
  [
    '(`[^`\\n]+`)', // `code`
    '(\\*\\*(?!\\s)[^*\\n]+(?<!\\s)\\*\\*)', // **bold**
    '(\\*(?!\\s)[^*\\n]+(?<!\\s)\\*)', // *italic*
    '((?<!\\w)_(?!\\s)[^_\\n]+(?<!\\s)_(?!\\w))', // _italic_
    '(\\[[^\\]\\n]*\\]\\([^)\\s]+\\))', // [label](href)
  ].join('|'),
  'g',
);

const LINK = /^\[([^\]]*)\]\(([^)\s]+)\)$/;

/**
 * Allow only http(s), mailto and site-relative links. Anything else — notably
 * `javascript:` and `data:` — is rendered as plain text rather than a link.
 */
export function safeHref(raw: string): string | null {
  const href = raw.trim();
  if (/^https?:\/\//i.test(href) || /^mailto:/i.test(href)) return href;
  if (href.startsWith('/') && !href.startsWith('//')) return href;
  return null;
}

export function renderInlineMarkdown(text: string): ReactNode {
  if (!text) return null;

  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of text.matchAll(TOKEN)) {
    const token = match[0];
    const start = match.index;

    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));
    lastIndex = start + token.length;

    if (token.startsWith('`')) {
      nodes.push(
        <code
          key={key++}
          className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[0.9em] dark:bg-zinc-800"
        >
          {token.slice(1, -1)}
        </code>,
      );
      continue;
    }

    if (token.startsWith('**')) {
      nodes.push(
        <strong key={key++} className="font-bold">
          {token.slice(2, -2)}
        </strong>,
      );
      continue;
    }

    if (token.startsWith('*') || token.startsWith('_')) {
      nodes.push(<em key={key++}>{token.slice(1, -1)}</em>);
      continue;
    }

    const link = LINK.exec(token);
    const href = link?.[2] ? safeHref(link[2]) : null;
    if (link && href) {
      const isExternal = /^https?:\/\//i.test(href);
      nodes.push(
        <a
          key={key++}
          href={href}
          className="font-medium text-amber-600 underline decoration-amber-400/40 underline-offset-2 transition-colors hover:decoration-amber-500 dark:text-amber-400"
          {...(isExternal ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
        >
          {link[1]}
        </a>,
      );
      continue;
    }

    // Unsupported scheme or malformed link: keep the author's literal text.
    nodes.push(token);
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}
